'use client';

import classnames from 'classnames';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { getCinemaInitials, getCinemaLogo, getDeterministicColorFromString } from '@/helpers/cinema-helper';
import { loadAsset } from '@cineamo/ui/src/helpers/asset-helper';

import { IconLocation24 } from '@cineamo/ui/src/icons/ic-location';

const defaultCinemaImage = loadAsset('/images/placeholders/im-cinemaDummy.png');

export type CinemaProfileImage = {
    className?: string;
    classNameInitials?: string;
    size?: '24' | '32' | '36' | '40' | '52' | '56' | '72' | '96';
    cinema: CinemaDto;
    href?: string;
    round?: boolean;
};

export default function CinemaProfileImage(props: CinemaProfileImage) {
    const { className, cinema, round } = props;

    const t = useTranslations();

    const size = props.size || '24';

    const [error, setError] = useState(false);

    // Render-time state sync: reset error when cinema changes
    const [prevCinema, setPrevCinema] = useState(cinema);
    if (prevCinema !== cinema) {
        setPrevCinema(cinema);
        setError(false);
    }

    if (!cinema) {
        return <IconLocation24 className="flex-none" />;
    }

    let classNameSize: string;
    let textSize: string;
    switch (size) {
        case '32':
            classNameSize = 'w-32 h-32';
            textSize = 'text-body-bold-12';
            break;
        case '36':
            classNameSize = 'w-36 h-36';
            textSize = 'text-body-bold-12';
            break;
        case '40':
            classNameSize = 'w-40 h-40';
            textSize = 'text-body-bold-12';
            break;
        case '52':
            classNameSize = 'w-52 h-52';
            textSize = 'text-body-bold-14';
            break;
        case '56':
            classNameSize = 'w-56 h-56';
            textSize = 'text-body-bold-14';
            break;
        case '72':
            classNameSize = 'w-72 h-72';
            textSize = 'text-body-bold-16';
            break;
        case '96':
            classNameSize = 'w-96 h-96';
            textSize = 'text-body-bold-16';
            break;
        default:
            classNameSize = 'w-24 h-24';
            textSize = 'text-body-bold-10';
            break;
    }

    const imageUrl = getCinemaLogo(cinema);

    return (
        <div
            className={classnames(
                className,
                classNameSize,
                'flex-none flex flex-col justify-center relative overflow-hidden rounded-full'
            )}
            style={{
                backgroundColor: cinema.primaryColor || getDeterministicColorFromString(cinema.name)
            }}
            title={cinema.name}>
            {!imageUrl ? (
                <div
                    className={`w-full h-full flex justify-center items-center transition ease-in duration-300 ${round ? 'rounded-full' : 'rounded-8'}`}>
                    <span className={classnames('text-white', props.classNameInitials || textSize)}>
                        {getCinemaInitials(cinema)}
                    </span>
                </div>
            ) : (
                <Image
                    className="w-full h-full object-cover"
                    src={!error && imageUrl ? imageUrl : defaultCinemaImage}
                    width={800}
                    height={800}
                    onError={() => setError(true)}
                    onErrorCapture={() => setError(true)}
                    alt={t('common.cinema-image-alt', { cinemaName: cinema.name })}
                />
            )}
        </div>
    );
}
